<template>
  <BlockUI :blocked="safeBlocked" v-bind="$attrs" />
</template>

<script setup lang="ts">
import BlockUI from 'primevue/blockui'
import { nextTick, onUnmounted, ref, watch } from 'vue'

interface Props {
  blocked: boolean
  [key: string]: any
}

const props = defineProps<Props>()

const safeBlocked = ref(props.blocked)
let updateTimer: ReturnType<typeof setTimeout> | null = null
let isUnmounted = false
let isUpdating = false

// 安全地更新 blocked 状态
const updateBlockedState = async (newValue: boolean) => {
  // 如果组件已卸载，不执行任何更新
  if (isUnmounted) {
    return
  }

  // 如果正在更新，跳过此次更新
  if (isUpdating) {
    return
  }

  if (updateTimer) {
    clearTimeout(updateTimer)
  }

  // 如果是从 true 变为 false，延迟更新避免 DOM 操作冲突
  if (safeBlocked.value && !newValue) {
    isUpdating = true
    updateTimer = setTimeout(async () => {
      // 再次检查组件是否已卸载
      if (!isUnmounted) {
        // 等待下一个 tick 确保 DOM 更新完成
        await nextTick()
        safeBlocked.value = newValue
        isUpdating = false
      } else {
        isUpdating = false
      }
    }, 300)
  } else if (!safeBlocked.value && newValue) {
    // 如果是从 false 变为 true，立即更新
    safeBlocked.value = newValue
  }
}

// 监听 blocked 属性变化
watch(
  () => props.blocked,
  (newValue) => {
    void updateBlockedState(newValue)
  },
  { immediate: true }
)

// 在组件卸载时清理
onUnmounted(() => {
  isUnmounted = true
  if (updateTimer) {
    clearTimeout(updateTimer)
  }
})

// 暴露组件实例方法
defineExpose({
  block: () => {
    void updateBlockedState(true)
  },
  unblock: () => {
    void updateBlockedState(false)
  }
})
</script>
